import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { customerIcon } from "@/components/MapView";
import { BaseMap, DEFAULT_CENTER, Dot, Pin } from "@/lib/mapProvider";
import { api, fmtDate } from "@/lib/api";

const COLORS = { online: "#34d399", paused: "#fbbf24", offline: "#71717a", gps_off: "#ef4444" };
const LABELS = { online: "Online", paused: "Paused", offline: "Offline", gps_off: "GPS off" };

const DriverPopup = ({ r }) => (
  <div data-testid={`live-driver-popup-${r.id}`} className="space-y-0.5 text-xs">
    <p className="text-sm font-semibold">{r.name}</p>
    <p>Driver ID {r.driver_code || "—"} · <span className="capitalize">{r.vehicle_class || "standard"}</span></p>
    <p>Status: <strong>{LABELS[r.status]}</strong></p>
    {r.status !== "online" && r.status !== "paused" && <p>Last seen {fmtDate(r.last_seen)}</p>}
    {r.current_trip ? <p>Current {r.current_trip.kind}: <Link to={`/trips/${r.current_trip.id}`}>{r.current_trip.status.replace("_", " ")} · {r.current_trip.customer_name}</Link></p> : <p>No current trip</p>}
  </div>
);

const Chips = ({ rows, filter, setFilter }) => (
  <div className="flex flex-wrap gap-2" data-testid="live-map-filters">
    {[["all", "All"], ...Object.entries(LABELS)].map(([k, l]) => (
      <button key={k} data-testid={`live-filter-${k}`} onClick={() => setFilter(k)}
        className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors ${filter === k ? "border-amber-400 bg-amber-400/10 text-amber-100" : "border-white/10 text-zinc-300 hover:border-amber-500/40"}`}>
        {k !== "all" && <span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[k] }} />}
        {l} <span data-testid={`live-count-${k}`} className="font-mono text-zinc-400">{k === "all" ? rows.length : rows.filter((r) => r.status === k).length}</span>
      </button>
    ))}
  </div>
);

export const LiveDriversMap = () => {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [updated, setUpdated] = useState(null);
  const [showCustomers, setShowCustomers] = useState(false);
  const [customers, setCustomers] = useState([]);
  useEffect(() => {
    if (!showCustomers) { setCustomers([]); return; }
    const get = () => api.get("/admin/live-customers").then(({ data }) => setCustomers(data)).catch(() => {});
    get(); const t = setInterval(get, 10000); return () => clearInterval(t);
  }, [showCustomers]);
  const load = useCallback(() => api.get("/admin/live-drivers").then(({ data }) => { setRows(data); setUpdated(new Date().toISOString()); }).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);
  const shown = rows.filter((r) => filter === "all" || r.status === filter);
  const placed = shown.filter((r) => r.lat != null);
  return (
    <div className="lux-card space-y-4 p-4 sm:p-6" data-testid="live-drivers-map">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="lux-eyebrow">Live drivers map · all approved drivers · auto-refresh 10s</p>
        <p className="text-xs text-zinc-500" data-testid="live-map-updated">Updated {fmtDate(updated)}</p>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Chips rows={rows} filter={filter} setFilter={setFilter} />
        <label className="inline-flex cursor-pointer items-center gap-2 text-xs text-amber-200">
          <input type="checkbox" data-testid="live-customers-toggle" checked={showCustomers} onChange={(e) => setShowCustomers(e.target.checked)} className="accent-amber-400" />
          Customers on active trips{showCustomers && <span data-testid="live-customers-count"> ({customers.length})</span>}
        </label>
      </div>
      <div className="relative z-0 overflow-hidden rounded-xl">
        <BaseMap center={DEFAULT_CENTER} zoom={10} className="h-[380px] w-full sm:h-[560px]">
          {placed.map((r) => (
            <Dot key={r.id} center={[r.lat, r.lng]} radius={9} stroke="#0b0b0d" strokeWeight={2} fill={COLORS[r.status]} fillOpacity={r.status === "online" || r.status === "paused" ? 0.95 : 0.6}>
              <DriverPopup r={r} />
            </Dot>
          ))}
          {customers.map((c) => (
            <Pin key={`c-${c.trip_id}`} position={[c.lat, c.lng]} icon={customerIcon}>
              <div data-testid={`live-customer-popup-${c.trip_id}`} className="text-xs">
                <b>{c.customer_name}</b><br />{c.kind} · {c.status}{c.driver_name ? ` · driver ${c.driver_name}` : ""}<br />
                {c.customer_location_off ? "Customer location off" : `Updated ${fmtDate(c.updated_at)}`}<br />
                <Link to={`/trips/${c.trip_id}`}>Open trip</Link>
              </div>
            </Pin>
          ))}
        </BaseMap>
      </div>
      {shown.some((r) => r.lat == null) && (
        <p data-testid="live-map-no-location" className="text-xs text-zinc-500">No location yet: {shown.filter((r) => r.lat == null).map((r) => `${r.name} (${r.driver_code || "—"})`).join(", ")}</p>
      )}
    </div>
  );
};
